import ChecklistOtherAttributesSettings from "./ChecklistOtherAttributesSettings";
import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Checkbox as MantineCheckbox,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IMinUserDepricated, IUserDepricated } from "@mctypes/global.types";
import { IconCalendarWeek, IconInfoCircle } from "@tabler/icons-react";
import { ColDef } from "ag-grid-community";
import React from "react";
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import { McTitle } from "../../../../components";
import { UserCellRenderer } from "../../../../components/ConnectedIssues/ConnectedIssues";
import FoldableSection from "../../../../components/FoldableSection/FoldableSection";
import { McAutocomplete } from "../../../../components/McAutocomplete";
import McInputNew from "../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../components/McInput/McTextAreaNew";
import { McMantineUserSelect } from "../../../../components/McMantineUserSelect/McMantineUserSelect";
import { SimpleTagsMantine } from "../../../../components/SimpleTags/SimpleTagsMantine";
import ViewEditTable from "../../../../components/ViewEditTable/ViewEditTable";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { useAppSelector } from "../../../../hooks";
import { ChecklistDisciplineRoles } from "@mcapps/shared/enums";
import { selectHttpProjectMembers } from "../../../projectSelectors";
import ChecklistTypes from "../admin/ChecklistTypes";
import StampConfiguration from "../admin/StampConfiguration";
import {
  useGetProjectByProjectIdChecklistTagsQuery,
  usePutProjectByProjectIdChecklistAndChecklistIdMutation,
} from "../../../../../openapi";
import {
  selectChecklistCategories,
  selectActiveChecklist,
  selectChecklistItems,
  selectChecklistSelectedPnsLabelForPage,
  selectChecklistSettingsMemberUsers,
  selectChecklistSettingsPnsAutocompleteData,
  selectChecklistSettingsPnsItems,
  selectChecklistSettingsUserComboboxData,
} from "../checklistSelectors";
import { ChecklistStatusEnum, CHECKLISTSTATUSES, competenceNetworkList, TEMPLATESTATUSES } from "../constants";

const ChecklistAdmin: React.FC = () => {
  const [updateChecklistMutation] = usePutProjectByProjectIdChecklistAndChecklistIdMutation();
  const { checklistId } = useParams<{ checklistId: string }>();
  const projectId = useAppSelector(selectCurrentProjectId);
  const projectMembers = useAppSelector(selectHttpProjectMembers);
  const checklist = useAppSelector((state) => selectActiveChecklist(state, checklistId));
  const checklistCategories = useAppSelector((state) => selectChecklistCategories(state, checklistId));
  const checklistItems = useAppSelector((state) => selectChecklistItems(state, checklistId));
  const memberUsers = useAppSelector(selectChecklistSettingsMemberUsers);
  const usersComboboxData = useAppSelector(selectChecklistSettingsUserComboboxData);
  const pnsItems = useAppSelector(selectChecklistSettingsPnsItems);
  const selectedPnsLabel = useAppSelector((state) => selectChecklistSelectedPnsLabelForPage(state, checklistId));
  const pnsAutocompleteData = useAppSelector(selectChecklistSettingsPnsAutocompleteData);

  const { data: tags = [] } = useGetProjectByProjectIdChecklistTagsQuery(
    { projectId: projectId ?? "" },
    { skip: projectId == null },
  );

  // Name must always be a string or crash in ag-grid cell editor when autocomplete is selecting empty/null user
  const emptyDocManager = { name: "", _id: "" } as IUserDepricated;
  const isNullOrEmpty = (value) => value == null || value === "";
  const removed = checklist?.status === ChecklistStatusEnum.REMOVED;

  if (checklist == null) {
    return (
      <Group justify="center" flex={1} p="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack id="a" h="100%" w="100%" p="sm" gap="sm" style={{ overflow: "auto" }}>
      <Paper withBorder radius={0} p="sm">
        <Group justify="space-between" align="flex-start" gap="sm">
          <div>
            <Title order={5}>{checklist.name}</Title>
            <Text size="sm" c="dimmed">
              {checklist.id != null ? `#${checklist.id}` : checklist._id}
            </Text>
          </div>
          <Group gap="xs">
            {checklist.isTemplate === true && <Badge variant="outline">Mal</Badge>}
            <Badge variant="light">{checklist.status}</Badge>
            <Badge variant="light" color="gray">
              {checklistCategories.length} kategorier
            </Badge>
            <Badge variant="light" color="gray">
              {checklistItems.length} rader
            </Badge>
          </Group>
        </Group>
      </Paper>

      {checklist.isTemplate === true && (
        <ChecklistAdminSection title="Malinstillinger" storageKey="checklist-admin-template-settings">
          <DocumentManagers
            onSave={(roles) =>
              updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: {
                  attribute: "documentManagers",
                  oldValue: {
                    preparedBy: checklist.documentManagers?.preparedBy?.name ?? "",
                    checkedBy: checklist.documentManagers?.checkedBy?.name ?? "",
                    approvedBy: checklist.documentManagers?.approvedBy?.name ?? "",
                  },
                  newValue: {
                    preparedBy: isNullOrEmpty(roles.preparedBy?._id) === false ? roles.preparedBy : null,
                    checkedBy: isNullOrEmpty(roles.checkedBy?._id) === false ? roles.checkedBy : null,
                    approvedBy: isNullOrEmpty(roles.approvedBy?._id) === false ? roles.approvedBy : null,
                  },
                },
              })
            }
            documentManagers={{
              preparedBy: checklist.documentManagers?.preparedBy ?? emptyDocManager,
              checkedBy: checklist.documentManagers?.checkedBy ?? emptyDocManager,
              approvedBy: checklist.documentManagers?.approvedBy ?? emptyDocManager,
            }}
            projectMembers={[
              {
                user: emptyDocManager,
              },
              ...projectMembers,
            ]}
          />
          <ChecklistField title="Admin">
            <McMantineUserSelect
              data={usersComboboxData}
              value={typeof checklist.admin === "string" ? checklist.admin : checklist.admin?._id ?? null}
              onChange={(value) =>
                updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: {
                    attribute: "admin",
                    oldValue: typeof checklist.admin === "string" ? checklist.admin : checklist.admin?.name,
                    newValue: value,
                  },
                })
              }
            />
          </ChecklistField>
          <ChecklistField title="FLF">
            <McMantineUserSelect
              data={usersComboboxData}
              value={
                typeof checklist.disciplineLeader === "string"
                  ? checklist.disciplineLeader
                  : checklist.disciplineLeader?._id ?? null
              }
              onChange={(value) =>
                updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: {
                    attribute: "disciplineLeader",
                    oldValue:
                      typeof checklist.disciplineLeader === "string"
                        ? checklist.disciplineLeader
                        : checklist.disciplineLeader?.name,
                    newValue: value,
                  },
                })
              }
            />
          </ChecklistField>
          <ChecklistField title="Fagdisiplin">
            <Select
              clearable
              searchable
              placeholder="Velg fagdisiplin"
              data={ChecklistDisciplineRoles.map((d) => ({ value: d.code, label: d.title }))}
              value={checklist.disciplineCode ?? null}
              onChange={(value) =>
                updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: { attribute: "disciplineCode", oldValue: checklist.disciplineCode, newValue: value },
                })
              }
            />
          </ChecklistField>
          <ChecklistField title="Delfagområde">
            <McInputNew
              type="text"
              onBlur={(e) =>
                updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: { attribute: "subDiscipline", oldValue: checklist.subDiscipline, newValue: e.target.value },
                })
              }
              value={checklist.subDiscipline}
              disabled={removed === true}
            />
          </ChecklistField>
          <ChecklistField title="Dokumenttype">
            <McInputNew
              type="text"
              onBlur={(e) =>
                updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: {
                    attribute: "documentCategoryCode",
                    oldValue: checklist.documentCategoryCode,
                    newValue: e.target.value,
                  },
                })
              }
              value={checklist.documentCategoryCode ?? "SKL"}
              disabled={true}
            />
          </ChecklistField>
          <ChecklistField title="Dokumenttype 2">
            <McInputNew
              type="text"
              onBlur={(e) =>
                updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: {
                    attribute: "documentCategoryCode2",
                    oldValue: checklist.documentCategoryCode2,
                    newValue: e.target.value,
                  },
                })
              }
              value={checklist.documentCategoryCode2}
              disabled={removed === true}
            />
          </ChecklistField>
          <ChecklistField title="Kompetansenettverk">
            <Select
              clearable
              searchable
              placeholder="Velg kompetansenettverk"
              data={competenceNetworkList}
              value={checklist.competenceNetwork ?? null}
              onChange={(value) =>
                updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: { attribute: "competenceNetwork", oldValue: checklist.competenceNetwork, newValue: value },
                })
              }
              disabled={removed === true}
            />
          </ChecklistField>
          <ChecklistField title="Revisionsnr">
            <McInputNew
              type="text"
              onBlur={(e) =>
                updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: { attribute: "revisionNr", oldValue: checklist.revisionNr, newValue: e.target.value },
                })
              }
              value={checklist.revisionNr}
              disabled={removed === true}
            />
          </ChecklistField>
          <ChecklistField title="Revisionsdato">
            <DateInput
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              clearable
              valueFormat="DD.MM.YYYY"
              value={checklist.revisionDate != null ? new Date(checklist.revisionDate) : null}
              onChange={(value) =>
                updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: {
                    attribute: "revisionDate",
                    oldValue: checklist.revisionDate,
                    newValue: value?.slice(0, 10) ?? null,
                  },
                })
              }
              rightSection={
                checklist.revisionDate == null && <IconCalendarWeek color="var(--mantine-color-warmgray-3)" />
              }
              disabled={removed === true}
            />
          </ChecklistField>
          <ChecklistField title="Malbeskrivelse">
            <McTextAreaNew
              value={checklist.templateDescription ?? ""}
              onBlur={(e) =>
                updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: {
                    attribute: "templateDescription",
                    oldValue: checklist.templateDescription,
                    newValue: e.target.value,
                  },
                })
              }
              disabled={removed === true}
              autosize
            />
          </ChecklistField>
        </ChecklistAdminSection>
      )}

      <ChecklistAdminSection title="Innstillinger" storageKey="checklist-admin-settings">
        <ChecklistField title="Navn">
          <McInputNew
            required={true}
            onBlur={(e) =>
              updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: { attribute: "name", oldValue: checklist.name, newValue: e.target.value },
              })
            }
            value={checklist.name}
            disabled={removed === true}
          />
        </ChecklistField>
        <ChecklistField title="Status">
          <Select
            allowDeselect={false}
            data={(checklist.isTemplate === true ? TEMPLATESTATUSES : CHECKLISTSTATUSES).filter(
              (x) => x.value !== "REMOVED",
            )}
            value={checklist.status}
            onChange={(newValue) =>
              updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: { attribute: "status", oldValue: checklist.status, newValue: newValue },
              })
            }
            disabled={removed === true}
          />
        </ChecklistField>
        <ChecklistField title="Fra mal">
          <McInputNew disabled={true} value={checklist.generatedFromTemplate?.name ?? ""} />
        </ChecklistField>
        <ChecklistField title="Etiketter på sjekkliste">
          <SimpleTagsMantine
            editable={removed === false}
            activeTags={checklist.tags || []}
            tagOptions={tags.map((t) => t._id)}
            onChange={async (tags) => {
              await updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: { attribute: "tags", oldValue: checklist.tags, newValue: tags },
              });
            }}
          />
        </ChecklistField>
        <ChecklistField title="Etiketter på spørsmål">
          <Stack gap={6}>
            <MantineCheckbox
              checked={checklist.itemTagsActive === true}
              onChange={() =>
                updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: {
                    attribute: "itemTagsActive",
                    oldValue: checklist.itemTagsActive,
                    newValue: checklist.itemTagsActive === true ? false : true,
                  },
                })
              }
            />
            {checklist.itemTagsActive === true && (
              <SimpleTagsMantine
                editable={removed === false}
                activeTags={checklist.itemTags || []}
                tagOptions={checklist.itemTags || []}
                onChange={(itemTags) => {
                  const unique = [...new Set(itemTags)];
                  if (JSON.stringify(unique) !== JSON.stringify(checklist.itemTags)) {
                    updateChecklistMutation({
                      projectId,
                      checklistId: checklist._id,
                      body: { attribute: "itemTags", oldValue: checklist.itemTags, newValue: unique },
                    });
                  }
                }}
              />
            )}
          </Stack>
        </ChecklistField>
        <ChecklistField title="Aktiver IR på opprinnelige roller">
          <MantineCheckbox
            checked={checklist.includeIrOnDefaultRoles}
            onChange={() =>
              updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: {
                  attribute: "includeIrOnDefaultRoles",
                  oldValue: checklist.includeIrOnDefaultRoles,
                  newValue: !checklist.includeIrOnDefaultRoles,
                },
              })
            }
          />
        </ChecklistField>
        <ChecklistField title="Aktiver uavklart (UAV) på opprinnelige roller">
          <MantineCheckbox
            checked={checklist.includeUavOnDefaultRoles}
            onChange={() =>
              updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: {
                  attribute: "includeUavOnDefaultRoles",
                  oldValue: checklist.includeUavOnDefaultRoles,
                  newValue: !checklist.includeUavOnDefaultRoles,
                },
              })
            }
          />
        </ChecklistField>
        <ChecklistField title="Sjekklisteroller">
          <ChecklistTypes
            allowAllDisabled
            checkTypes={checklist.checkTypes}
            editable={removed === false}
            onChange={(types) =>
              updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: { attribute: "checkTypes", oldValue: JSON.stringify(checklist.checkTypes), newValue: types },
              })
            }
          />
        </ChecklistField>
        {checklist.isTemplate === false && (
          <>
            <ChecklistField title="Ansvarlig">
              <McMantineUserSelect
                data={usersComboboxData}
                value={
                  typeof checklist.assignedTo === "string" ? checklist.assignedTo : checklist.assignedTo?._id ?? null
                }
                disabled={removed === true}
                onChange={(value) =>
                  updateChecklistMutation({
                    projectId,
                    checklistId: checklist._id,
                    body: {
                      attribute: "assignedTo",
                      oldValue:
                        typeof checklist.assignedTo === "string" ? checklist.assignedTo : checklist.assignedTo?.name,
                      newValue: value == null ? null : memberUsers.find((user) => user._id === value) ?? null,
                    },
                  })
                }
              />
            </ChecklistField>
            <ChecklistField title="Tidsfrist">
              <DateInput
                styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
                clearable
                valueFormat="DD.MM.YYYY"
                value={checklist.dueDate != null ? new Date(checklist.dueDate) : null}
                onChange={(value) =>
                  updateChecklistMutation({
                    projectId,
                    checklistId: checklist._id,
                    body: {
                      attribute: "dueDate",
                      oldValue: checklist.dueDate,
                      newValue: value != null ? value : undefined,
                    },
                  })
                }
                rightSection={checklist.dueDate == null && <IconCalendarWeek color="var(--mantine-color-warmgray-3)" />}
                disabled={removed === true}
              />
            </ChecklistField>
            <ChecklistField title="PNS">
              {pnsItems != null && (
                <McAutocomplete
                  clearable
                  data={pnsAutocompleteData}
                  value={selectedPnsLabel}
                  onClear={() =>
                    updateChecklistMutation({
                      projectId,
                      checklistId: checklist._id,
                      body: { attribute: "pns", oldValue: checklist.pns, newValue: "" },
                    })
                  }
                  onCommit={(value) => {
                    const selectedPns = pnsItems.find((p) => p._id === value || p.comboLabel === value);
                    updateChecklistMutation({
                      projectId,
                      checklistId: checklist._id,
                      body: { attribute: "pns", oldValue: checklist.pns, newValue: selectedPns?.props?.pnsId ?? "" },
                    });
                  }}
                />
              )}
            </ChecklistField>
          </>
        )}
      </ChecklistAdminSection>

      {projectId && <ChecklistOtherAttributesSettings projectId={projectId} checklistId={checklist._id} />}

      <ChecklistAdminSection title="Rapportinfo" storageKey="checklist-admin-report-settings">
        <ChecklistField title="Fase i oppdraget">
          <McInputNew
            onBlur={(e) =>
              updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: { attribute: "phase", oldValue: checklist.phase, newValue: e.target.value },
              })
            }
            value={checklist.phase ?? ""}
            disabled={removed === true}
          />
        </ChecklistField>
        <ChecklistField title="Disiplin/fag">
          <McInputNew
            onBlur={(e) =>
              updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: { attribute: "discipline", oldValue: checklist.discipline, newValue: e.target.value },
              })
            }
            value={checklist.discipline ?? ""}
            disabled={removed === true}
          />
        </ChecklistField>
        <ChecklistField title="Navn/nr. kontrollert dokument/aktivitet">
          <McInputNew
            onBlur={(e) =>
              updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: { attribute: "activity", oldValue: checklist.activity, newValue: e.target.value },
              })
            }
            value={checklist.activity ?? ""}
            disabled={removed === true}
          />
        </ChecklistField>
        <ChecklistField title="Beskrivelse">
          <McTextAreaNew
            value={checklist.aboutText}
            onBlur={(e) =>
              updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: { attribute: "aboutText", oldValue: checklist.aboutText, newValue: e.target.value },
              })
            }
            disabled={removed === true}
            autosize
          />
        </ChecklistField>
        <ChecklistField title="Rapporttekst">
          <StampConfiguration
            currentConfig={checklist.stampSettings}
            onChange={(newStampSettings) =>
              updateChecklistMutation({
                projectId,
                checklistId: checklist._id,
                body: { attribute: "stampSettings", oldValue: "", newValue: newStampSettings },
              })
            }
          />
        </ChecklistField>
      </ChecklistAdminSection>

      <ChecklistRemoveRefactored />
    </Stack>
  );
};

export default ChecklistAdmin;

const ChecklistRemoveRefactored: React.FC = () => {
  const [updateChecklistMutation] = usePutProjectByProjectIdChecklistAndChecklistIdMutation();
  const history = useHistory();
  const { checklistId } = useParams<{ checklistId: string }>();
  const projectId = useAppSelector(selectCurrentProjectId);
  const checklist = useAppSelector((state) => selectActiveChecklist(state, checklistId));
  const [removeModalOpen, setRemoveModalOpen] = React.useState<boolean>(false);
  const [removeNameCheck, setRemoveNameCheck] = React.useState<string>("");

  if (checklist == null) {
    return null;
  }

  const disabled = checklist.status === ChecklistStatusEnum.REMOVED;

  const openModal = () => {
    setRemoveNameCheck("");
    setRemoveModalOpen(true);
  };

  const removeChecklist = async () => {
    if (projectId == null) return;

    try {
      await updateChecklistMutation({
        projectId,
        checklistId: checklist._id,
        body: { attribute: "status", oldValue: checklist.status, newValue: ChecklistStatusEnum.REMOVED },
      }).unwrap();
    } catch {
      return;
    }

    setRemoveModalOpen(false);
    history.push(`/project/${projectId}/checklist`);
  };

  return (
    <>
      <Card w="100%" p="sm" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)" style={{ overflow: "visible" }}>
        <Stack gap="xs">
          <Title order={4} fw={400} lh={1.25} m={0}>
            Slett {checklist.isTemplate === true ? "mal" : "sjekkliste"}
          </Title>
          <Button
            variant="danger-filled"
            onClick={openModal}
            disabled={disabled}
            w="150px"
            leftSection={<i className="fa fa-remove fa-fw" />}
          >
            Slett
          </Button>
        </Stack>
      </Card>
      <Modal
        opened={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        title={`Slett ${checklist.name}`}
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            Bekreft navnet på {checklist.isTemplate === true ? "malen" : "sjekklisten"} som skal slettes
          </Text>
          <TextInput value={removeNameCheck} onChange={(e) => setRemoveNameCheck(e.currentTarget.value)} />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => setRemoveModalOpen(false)}>
              Avbryt
            </Button>
            <Button
              variant="danger-filled"
              disabled={removeNameCheck.toLocaleLowerCase() !== checklist.name.toLocaleLowerCase()}
              onClick={removeChecklist}
              leftSection={<i className="fa fa-trash fa-fw" />}
            >
              Slett
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

const ChecklistAdminSection: React.FC<{
  title: string;
  storageKey: string;
  children: React.ReactNode;
}> = ({ title, storageKey, children }) => {
  return (
    <FoldableSection storageKey={storageKey}>
      <FoldableSection.Header>
        <Title order={6}>{title}</Title>
      </FoldableSection.Header>
      <FoldableSection.Body>
        <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
          <Stack gap="sm" maw="35rem">
            {children}
          </Stack>
        </Card>
      </FoldableSection.Body>
    </FoldableSection>
  );
};

const ChecklistField: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  return <McTitle title={title}>{children}</McTitle>;
};

export interface IDocumentManagers {
  preparedBy: IMinUserDepricated;
  checkedBy: IMinUserDepricated;
  approvedBy: IMinUserDepricated;
}

interface IDocumentManagerProps {
  documentManagers: IDocumentManagers;
  projectMembers: DocumentManagerMember[];
  onSave: (data: IDocumentManagers) => void;
}

type DocumentManagerMember = {
  user: IMinUserDepricated;
};

const buildDocumentManagerColumns = (projectMembers: DocumentManagerMember[]): ColDef[] => [
  {
    headerName: "Rolle",
    field: "role",
    editable: false,
  },
  {
    headerName: "Bruker",
    field: "user",
    editable: true,
    cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
    cellEditor: "agRichSelectCellEditor",
    valueGetter: (params) => {
      return params.data.user ?? null;
    },
    cellEditorParams: {
      cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
      formatValue: (value) => value?.name ?? "",
      values: projectMembers.map((m) => m.user),
      allowTyping: true,
      filterList: true,
    },
  },
];

const getDocumentManagerRows = (documentManagers: IDocumentManagers) => [
  {
    role: "Utarbeidet av",
    user: documentManagers?.preparedBy,
  },
  {
    role: "Kontrollert av",
    user: documentManagers?.checkedBy,
  },
  {
    role: "Godkjent av",
    user: documentManagers?.approvedBy,
  },
];

const DocumentManagers: React.FC<IDocumentManagerProps> = ({ onSave, documentManagers, projectMembers }) => {
  const saveChanges = (newData) => {
    onSave({
      preparedBy: newData[0].user,
      checkedBy: newData[1].user,
      approvedBy: newData[2].user,
    });
  };

  const _columns = buildDocumentManagerColumns(projectMembers);
  const _data = getDocumentManagerRows(documentManagers);

  return (
    <Stack gap="xs">
      <Group gap={6} align="center">
        <Text fw={500}>Dokumentansvarlige</Text>
        <Tooltip label='Legg til flere brukere under "Prosjektmedlemmer"' withArrow>
          <IconInfoCircle size={16} color="var(--mantine-color-blue-6)" />
        </Tooltip>
      </Group>
      <ViewEditTable
        gridOptions={{
          popupParent: document.body,
        }}
        data={_data}
        columns={_columns}
        onSave={saveChanges}
        newItemTemplateObject={{
          role: "",
          user: null,
        }}
        canEdit={true}
        canAddRows={false}
      />
    </Stack>
  );
};
